// The writes the pull request page sends straight to GitHub (resolve a
// thread, comment, merge, ask for a review) and the attention overrides
// it saves on the changelist server (see pr/host.ts).
import { graphql, restWrite } from '../../api/gh';
import { ADD_COMMENT, MERGE_PR, RESOLVE_THREAD, UNRESOLVE_THREAD } from '../../api/queries';
import { clearAttention, putAttention } from '../../api/server';
import { stepKey } from '../../logic/aftermath';
import type { AttentionChange } from '../../logic/attention';
import { compareTimes } from '../../logic/time';
import { dedup, subject, withAttention, withIssueComment, withResolved } from '../optimistic';
import { session } from '../session.svelte';
import type { Draft, HandOffWrite, WriteHost } from './host';

export class PrWrites {
  /** `superseded` is told the hand-off steps (`stepKey`) a write made by hand retires: only the latest intent counts. */
  constructor(
    private readonly h: WriteHost,
    private readonly superseded: (keys: string[]) => void,
  ) {}

  /** Resolve or unresolve a thread: shown at once, the thread marked as being resolved until GitHub confirms. */
  setResolved(threadId: string, resolved: boolean) {
    return this.h.write({
      kind: resolved ? 'resolve' : 'unresolve',
      dedup: dedup.thread(threadId),
      label: resolved ? 'Resolving' : 'Unresolving',
      done: resolved ? 'Thread resolved' : 'Thread reopened',
      subject: subject.thread(threadId),
      apply: withResolved(threadId, resolved, this.h.viewer),
      send: async () => {
        await graphql(resolved ? RESOLVE_THREAD : UNRESOLVE_THREAD, { input: { threadId } });
      },
    });
  }

  /** A conversation comment, shown at once at the end of the conversation. */
  addIssueComment(body: string, draft: Draft) {
    const id = this.h.tempId();
    return this.h.write({
      kind: 'comment',
      dedup: dedup.draft(draft.key),
      label: 'Commenting',
      done: 'Comment posted',
      subject: subject.comment(id),
      draft,
      apply: withIssueComment({ id, author: this.h.me(), body, at: new Date().toISOString() }),
      send: async () => {
        await graphql(ADD_COMMENT, { input: { subjectId: this.h.server.pr!.id, body } });
      },
    });
  }

  /**
   * Merge. The PR is not shown merged before GitHub says so (a merge it
   * refuses would have shown a false outcome): the merge card shows it in
   * flight instead.
   */
  merge(method: 'MERGE' | 'SQUASH' | 'REBASE') {
    const pr = this.h.server.pr!;
    return this.h.write({
      kind: 'merge',
      dedup: dedup.merge,
      label: 'Merging',
      done: 'Merged',
      subject: subject.merge,
      send: async () => {
        await graphql(MERGE_PR, { input: { pullRequestId: pr.id, mergeMethod: method, expectedHeadOid: pr.headRefOid } });
      },
    });
  }

  /** Ask `login` to review (again): `POST requested_reviewers`. */
  requestReviewer(login: string, how?: HandOffWrite) {
    // A request made by hand retires the hand-off's for this login (latest intent).
    if (!how) this.superseded([stepKey({ kind: 'review', login })]);
    return this.h.write({
      onError: how?.onError,
      unlisted: how?.unlisted,
      kind: 'request-review',
      dedup: dedup.reviewRequest(login),
      label: 'Requesting review',
      done: `Review requested from ${login}`,
      subject: subject.reviewRequest,
      apply: (s) =>
        s.pr
          ? {
              ...s,
              pr: {
                ...s.pr,
                reviewRequests: { nodes: [...s.pr.reviewRequests.nodes, { requestedReviewer: { __typename: 'User', login } }] },
              },
            }
          : s,
      send: async () => {
        const pr = await restWrite<{ updated_at?: string } | null>('POST', `repos/${this.h.owner}/${this.h.repo}/pulls/${this.h.number}/requested_reviewers`, {
          reviewers: [login],
        });
        if (pr?.updated_at) how?.onTime?.(pr.updated_at);
      },
    });
  }

  setAttention(login: string, state: 'add' | 'remove' | null) {
    return this.changeAttention([{ login, state }]);
  }

  /**
   * Write attention overrides (basis: the latest timeline event), one write
   * per login, labelled with the login ("Updating attention for @alice").
   * Each is shown at once and confirmed by the next fetch of the overrides.
   * Only the latest intent per login counts: a change for a login whose
   * write is still being sent waits until the server answers it (not for
   * the reload), replacing any change already waiting, which is never
   * sent; so the server ends at the last change made. A failed write rolls
   * back its login, and the overrides are fetched again (the server may
   * have saved it).
   */
  changeAttention(changes: AttentionChange[], how?: HandOffWrite): Promise<boolean> {
    if (!changes.length) return Promise.resolve(true);
    // A change made by hand retires the hand-off's for these logins (latest intent).
    if (!how) this.superseded(changes.map((c) => stepKey({ kind: 'attention', change: c })));
    if (!this.h.serverData) {
      how?.onError({ text: this.h.serverDataText ?? 'Attention changes are off here', link: null });
      return Promise.resolve(false);
    }
    // The hand-off's overrides go on the review's time when the timeline does not list the review yet.
    const latest = this.h.latestEventAt();
    const basis = how?.basis && compareTimes(how.basis, latest) > 0 ? how.basis : latest;
    const me = session.user;
    const written = { basis, set_by: me ? { id: me.id, login: me.login } : null, set_at: new Date().toISOString() };
    return Promise.all(
      changes.map((c) =>
        this.h.writes.run({
          kind: 'attention',
          dedup: dedup.attention(c.login),
          latest: true,
          label: `Updating attention for @${c.login}`,
          done: 'Attention updated',
          subject: subject.attention,
          onError: how?.onError,
          unlisted: how?.unlisted,
          apply: withAttention([c], written),
          send: async () => {
            if (c.state === null) await clearAttention(this.h.owner, this.h.repo, this.h.number, c.login);
            else await putAttention(this.h.owner, this.h.repo, this.h.number, c.login, c.state, basis);
          },
          confirm: {
            reload: 'overrides',
            ticket: () => this.h.overridesReads,
            run: async () => {
              await this.h.loadOverrides();
              // Accepted after server data turned off: the overrides are never fetched again.
              if (!this.h.serverData || this.h.disposed) this.h.writes.settle('overrides', Infinity);
            },
          },
        }),
      ),
    ).then((oks) => oks.every(Boolean));
  }
}
